import { useState, useRef, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Search, Sparkles, Mic, Square, Loader2, Volume2, VolumeX, X } from "lucide-react";
import { toast } from "sonner";
import { VoicePicker } from "@/components/VoicePicker";
import { SpeakingIndicator } from "@/components/SpeakingIndicator";
import { getVoice, saveVoice } from "@/lib/voices";
import { narrateUrl } from "@/lib/narrate";
import { startNarration, endNarration, stopNarration } from "@/lib/narrator";

const NARRATE_KEY = "erio_nlq_narrate";

const CHIPS = [
  "top 5 risks",
  "risks in cyber",
  "open premortem alerts",
  "tier 1 suppliers in DE",
  "signals from recorded_future",
];

const SEV_COLOR = {
  critical: "text-red-600",
  high: "text-orange-600",
  medium: "text-amber-600",
  low: "text-ek-ink/65",
};

const INTENT_LABEL = {
  ai_answer: "AI answer",
  open_premortem_alerts: "Open pre-mortem alerts",
  suppliers: "Suppliers",
  signals: "Signals",
  risks_in_theme: "Risks",
};

const itemLabel = (it) =>
  it.title || it.legal_name || it.summary || it.risk_title || it.name || it.risk_id || "";

// One-line spoken/displayed summary for a structured (non-AI) result set.
function buildSummary(result) {
  const n = result.items?.length || 0;
  const label = INTENT_LABEL[result.intent] || "Results";
  if (!n) return "No matching results for that query.";
  const names = result.items.slice(0, 3).map(itemLabel).filter(Boolean);
  const head = `${label}: ${n} result${n === 1 ? "" : "s"}.`;
  return names.length ? `${head} Top ${names.length}: ${names.join("; ")}.` : head;
}

function ResultRow({ intent, item }) {
  const primary =
    item.title || item.legal_name || item.summary || item.risk_title || item.risk_id;
  let meta = null;
  if (intent === "suppliers") meta = `T${item.tier} · ${item.country}`;
  else if (intent === "signals") meta = (
    <span className={`font-semibold uppercase ${SEV_COLOR[item.severity] || ""}`}>{item.severity}</span>
  );
  else if (intent === "open_premortem_alerts") meta = `${Math.round((item.confidence || 0) * 100)}% conf`;
  else if (item.residual_score != null) meta = `residual ${item.residual_score}`;

  return (
    <div
      data-testid="nlq-result-row"
      className="flex items-center justify-between gap-3 border-b border-black/5 px-4 py-2.5 last:border-0"
    >
      <span className="truncate text-sm text-ek-ink">{primary}</span>
      {meta && <span className="shrink-0 text-xs text-ek-ink/65">{meta}</span>}
    </div>
  );
}

export const NLQBar = () => {
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [narrate, setNarrate] = useState(() => localStorage.getItem(NARRATE_KEY) !== "0");
  const [speaking, setSpeaking] = useState(false);
  const [voice, setVoice] = useState(getVoice);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const narrateRef = useRef(narrate);
  narrateRef.current = narrate;

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); endNarration(audioRef.current); audioRef.current = null; }
    setSpeaking(false);
  };

  const toggleNarrate = () => {
    setNarrate((v) => {
      const next = !v;
      localStorage.setItem(NARRATE_KEY, next ? "1" : "0");
      if (!next) stopAudio();
      return next;
    });
  };

  // Speak an answer aloud via OpenAI TTS (nova). Toggling while playing stops it.
  const speak = async (text) => {
    if (speaking && audioRef.current) { stopAudio(); return; }
    stopNarration();   // never overlap with another narration
    if (!text) return;
    setSpeaking(true);
    try {
      const src = await narrateUrl(text, voice);
      const audio = new Audio(src);
      audioRef.current = audio;
      startNarration(audio, () => { audioRef.current = null; setSpeaking(false); });
      audio.onended = () => { endNarration(audio); audioRef.current = null; setSpeaking(false); };
      // play() can reject if interrupted by another narration or autoplay policy —
      // that's expected, so swallow it (don't show a "narration unavailable" toast).
      try { await audio.play(); } catch { /* interrupted / blocked — ignore */ }
    } catch (err) {
      setSpeaking(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Narration unavailable.");
    }
  };

  const clearResult = () => { stopAudio(); setResult(null); };

  const run = async (question) => {
    const text = (question ?? q).trim();
    if (!text) return;
    setQ(text);
    setLoading(true);
    stopAudio();
    try {
      const { data } = await api.post("/nlq", { question: text });
      const r = { intent: data.intent, items: data.results, answer: data.answer };
      setResult(r);
      if (narrateRef.current) {
        if (data.intent === "ai_answer" && data.answer) speak(data.answer);
        else if (data.results?.length) speak(buildSummary(r));
      }
    } catch (err) {
      setResult({ error: err.response?.data?.detail || "Unsupported query." });
    } finally {
      setLoading(false);
    }
  };

  const sendVoice = async (blob) => {
    setTranscribing(true);
    stopAudio();
    try {
      const form = new FormData();
      form.append("audio", blob, "query.webm");
      const { data } = await api.post("/nlq/voice", form);
      if (data.transcript) setQ(data.transcript);
      const r = { intent: data.intent, items: data.results, answer: data.answer };
      setResult(r);
      // Voice in → voice out.
      if (narrateRef.current) {
        if (data.intent === "ai_answer" && data.answer) speak(data.answer);
        else if (data.results?.length) speak(buildSummary(r));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Voice query failed.");
    } finally {
      setTranscribing(false);
    }
  };

  const stopRecording = () => {
    recorderRef.current?.state === "recording" && recorderRef.current.stop();
    setRecording(false);
  };

  const toggleRecording = async () => {
    if (recording) {
      stopRecording();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 0) sendVoice(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  return (
    <section className="mt-12" data-testid="nlq-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="ek-eyebrow flex items-center gap-1">
            <Sparkles size={12} /> Ask ERIO
          </h2>
          <p className="mt-1 text-sm text-ek-ink/65">
            Natural-language queries over your risk, supplier and signal data — type or speak.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <VoicePicker
            value={voice}
            onChange={(v) => { setVoice(v); saveVoice(v); stopAudio(); }}
            testid="nlq-voice-picker"
          />
          <button
            onClick={toggleNarrate}
            data-testid="nlq-narrate-toggle"
            aria-pressed={narrate}
            title={narrate ? "Spoken answers are on — click to mute" : "Spoken answers are off — click to enable"}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              narrate
                ? "border-ek-purple/30 bg-ek-purple/10 text-ek-purple-deep"
                : "border-black/10 bg-white text-ek-ink/65 hover:border-ek-purple/30"
            }`}
          >
            {narrate ? <Volume2 size={14} /> : <VolumeX size={14} />}
            {narrate ? "Narration on" : "Narration off"}
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 rounded-md border border-black/10 bg-white px-4 py-2.5 focus-within:border-ek-purple focus-within:ring-2 focus-within:ring-ek-purple/20">
        <Search size={18} className="text-ek-ink/30" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder={recording ? "Listening… click stop when done" : "e.g. top 5 risks"}
          data-testid="nlq-input"
          className="flex-1 bg-transparent text-sm text-ek-ink outline-none placeholder:text-ek-ink/30"
        />
        <button
          onClick={toggleRecording}
          disabled={loading || transcribing}
          data-testid="nlq-voice-button"
          title={recording ? "Stop recording" : "Ask by voice"}
          className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:opacity-50 ${
            recording
              ? "bg-red-600 text-white animate-pulse"
              : "text-ek-ink/65 hover:bg-ek-purple/10 hover:text-ek-purple"
          }`}
        >
          {transcribing ? (
            <Loader2 size={16} className="animate-spin" />
          ) : recording ? (
            <Square size={15} />
          ) : (
            <Mic size={16} />
          )}
        </button>
        <button
          onClick={() => run()}
          disabled={loading || transcribing}
          data-testid="nlq-submit"
          className="ek-btn !px-4 !py-1.5 text-sm disabled:opacity-50"
        >
          Ask
        </button>
      </div>

      {transcribing && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-ek-purple" data-testid="nlq-transcribing">
          <Loader2 size={12} className="animate-spin" /> Transcribing your question…
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {CHIPS.map((c) => (
          <button
            key={c}
            onClick={() => run(c)}
            data-testid={`nlq-chip-${c.replace(/[^a-z0-9]+/gi, "-")}`}
            className="rounded-full border border-black/10 px-3 py-1 text-xs text-ek-ink/60 transition-colors hover:border-ek-purple/40 hover:text-ek-purple"
          >
            {c}
          </button>
        ))}
      </div>

      {result && (
        <div className="mt-4 overflow-hidden rounded-md border border-black/5 bg-white" data-testid="nlq-results">
          {result.error ? (
            <div className="flex items-start justify-between gap-2 px-4 py-4">
              <p className="text-sm text-red-600" data-testid="nlq-error">{result.error}</p>
              <button onClick={clearResult} data-testid="nlq-close-answer" aria-label="Close" className="shrink-0 text-ek-ink/65 hover:text-ek-ink">
                <X size={15} />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 border-b border-black/5 px-4 py-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-ek-purple/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ek-purple">
                  {result.intent === "ai_answer" ? <Sparkles size={10} /> : null}
                  {INTENT_LABEL[result.intent] || "Results"}
                  {speaking && <SpeakingIndicator className="ml-0.5 h-3 text-ek-purple" />}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => speak(result.intent === "ai_answer" ? result.answer : buildSummary(result))}
                    data-testid="nlq-listen"
                    className={`flex items-center gap-1 text-[11px] font-semibold transition-colors hover:text-ek-purple ${speaking ? "text-ek-purple" : "text-ek-ink/65"}`}
                  >
                    {speaking ? <Square size={11} /> : <Volume2 size={12} />}
                    {speaking ? "Stop" : "Listen"}
                  </button>
                  <button
                    onClick={clearResult}
                    data-testid="nlq-close-answer"
                    title="Close (stops narration)"
                    aria-label="Close answer"
                    className="text-ek-ink/65 transition-colors hover:text-ek-ink"
                  >
                    <X size={15} />
                  </button>
                </div>
              </div>
              {result.intent === "ai_answer" ? (
                <p className="whitespace-pre-line px-4 py-4 text-sm leading-relaxed text-ek-ink" data-testid="nlq-ai-answer">
                  {result.answer}
                </p>
              ) : result.items.length === 0 ? (
                <p className="px-4 py-4 text-sm text-ek-ink/65">No matching results.</p>
              ) : (
                result.items.map((item, i) => (
                  <ResultRow key={i} intent={result.intent} item={item} />
                ))
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};
